import React from 'react';
import { Search, X, Filter } from 'lucide-react';

const Select = ({ label, value, onChange, children }) => (
  <div>
    <label className="block text-xs font-medium text-slate-400 mb-1">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-slate-900 border border-slate-600 text-slate-200 rounded-md px-3 py-2 text-sm min-w-[10rem]
                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    >
      {children}
    </select>
  </div>
);

const FilterBar = ({ filters, onChange, onReset, voiders, carriers, matchCount, totalCount }) => {
  const set = (patch) => onChange({ ...filters, ...patch });

  const isFiltered = filters.search || filters.status !== 'all'
    || filters.voidedBy !== 'all' || filters.carrier !== 'all' || filters.reshipped !== 'all';

  return (
    <div className="flex-shrink-0 bg-slate-800 border border-slate-700 rounded-lg p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[14rem]">
          <label htmlFor="void-search" className="block text-xs font-medium text-slate-400 mb-1">
            Search
          </label>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              id="void-search"
              type="text"
              value={filters.search}
              onChange={(e) => set({ search: e.target.value })}
              placeholder="Order number, tracking number, or user…"
              className="w-full bg-slate-900 border border-slate-600 text-slate-200 rounded-md pl-9 pr-8 py-2 text-sm
                         placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {filters.search && (
              <button
                onClick={() => set({ search: '' })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <Select label="Status" value={filters.status} onChange={(v) => set({ status: v })}>
          <option value="all">All rows</option>
          <option value="V">Voids only (V)</option>
          <option value="S">Replacements only (S)</option>
        </Select>

        <Select label="Reshipped" value={filters.reshipped} onChange={(v) => set({ reshipped: v })}>
          <option value="all">Any</option>
          <option value="yes">Reshipped</option>
          <option value="no">Never reshipped</option>
        </Select>

        <Select label="Voided by" value={filters.voidedBy} onChange={(v) => set({ voidedBy: v })}>
          <option value="all">Everyone</option>
          {voiders.map((v) => (
            <option key={v.name} value={v.name}>{v.name} ({v.count})</option>
          ))}
        </Select>

        <Select label="Carrier" value={filters.carrier} onChange={(v) => set({ carrier: v })}>
          <option value="all">All carriers</option>
          {carriers.map((c) => (
            <option key={c.name} value={c.name}>{c.name} ({c.count})</option>
          ))}
        </Select>

        {isFiltered && (
          <button
            onClick={onReset}
            className="px-3 py-2 rounded-md text-sm bg-slate-700 text-slate-300 hover:bg-slate-600 flex items-center gap-1.5"
          >
            <X className="w-4 h-4" />
            Clear
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-700/60 text-xs text-slate-500">
        <Filter className="w-3.5 h-3.5" />
        <span>
          Showing <span className="text-slate-300 font-medium">{matchCount.toLocaleString()}</span> of{' '}
          {totalCount.toLocaleString()} rows
          {filters.reshipped !== 'all' && filters.status === 'S' && (
            <span className="text-amber-400 ml-2">
              — the reshipped filter only applies to void rows, so this pairing returns nothing.
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

export default FilterBar;
